<template>
  <div class="qc-playform-modal qc-forgot-password-modal-warp">
    <div class="qc-modal-warp">
      <div class="qc-playform-overlay"></div>
      <div class="qc-playform-layout">
        <Header closeBtnIcon="retrun_btn" :closeHandle="props.onClose" />

        <div class="qc-playform-content qc-forgot-password-content">
          <div class="qc-title qc-text-center">{{ language[LANGUAGE_KEY.TITLE_RECOVER_PASSWORD] }}</div>
          <div class="qc-playform-form">
            <div class="qc-form-item">
              <div class="qc-form-item-label">
                <div class="qc-sprites qc-sprites-email_icon"></div>
              </div>
              <div class="qc-form-input">
                <input type="text"
                  style="width: 100%;"
                  :placeholder="language[LANGUAGE_KEY.PROMPT_INPUT_EMAIL]"
                  name="email"
                  v-model.trim="email"
                  autocomplete="off"
                  @focus="() => onFocus(1)"
                  @blur="onBlur" />
              </div>
            </div>
            <div class="qc-form-item qc-code-form-item">
              <div class="qc-form-item-label">
                <div class="qc-sprites qc-sprites-safe_icon"></div>
              </div>
              <div class="qc-form-input">
                <input
                  type="text"
                  v-model.trim="code"
                  :placeholder="language[LANGUAGE_KEY.VERIFICATION_CODE_INPUT_PLACEHOLDER]"
                  name="code"
                  autocomplete="off"
                  @focus="() => onFocus(2)"
                  @blur="onBlur" />
              </div>
              <div class="qc-form-operation">
                <div v-if="canSendCode" class="qc-send-code-text" @click="sendCode()">{{ language[LANGUAGE_KEY.DESC_SEND] }}</div>
                <div v-else class="qc-send-code-disable">
                  {{ countdown + 's ' + language[LANGUAGE_KEY.DESC_RESEND] }}
                </div>
              </div>
            </div>
            <div class="qc-form-item">
              <div class="qc-form-item-label">
                <div class="qc-sprites qc-sprites-password_icon"></div>
              </div>
              <div class="qc-form-input">
                <input :type="showPassword ? 'text' : 'password'"
                  :placeholder="language[LANGUAGE_KEY.PASSWORD_INPUT_PLACEHOLDER]"
                  name="password"
                  v-model.trim="password"
                  autocomplete="off"
                  @input="handlePasswordInput"
                  @focus="() => onFocus(2)"
                  @blur="onBlur" />
              </div>
              <div class="qc-form-operation" @click="togglePassword()">
                <div v-if="showPassword" class="qc-sprites qc-sprites-password_show_icon"></div>
                <div v-else class="qc-sprites qc-sprites-password_hide_icon"></div>
              </div>
            </div>
            <div :class="'qc-default-btn qc-confirm-btn ' + (isFormInvalid ? 'qc-disable-btn' : '')" @click="onRecover()">
              {{ language[LANGUAGE_KEY.CONFIRM] }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed } from 'vue';
import { Toast } from '../../subComponents/Toast/index';
import Header from '../../subComponents/Header/index.vue';
import ApiManager from '../../core/api/index';
import configManager from '../../core/config/index';
import { LANGUAGE_KEY } from '../../core/constants/language';
import { VERIFY_CODE_TYPE, VERIFY_CODE_CHANNEL } from '../../core/constants/index';
import type { InternationalForgotPasswordProps } from '../types';

const props = withDefaults(defineProps<InternationalForgotPasswordProps>(), {});
const language = configManager.getLanguage();

const email = ref('');
const code = ref('');
const password = ref('');
const emailError = computed(() => {
  if (!email.value) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_EMAIL];
  }
  // 简单的正则表达式校验邮箱格式
  if (!/\S+@\S+\.\S+/.test(email.value)) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_EMAIL];
  }
});
const codeError = computed(() => {
  if (!code.value) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_VERIFICATION_CODE];
  }
  if (!new RegExp("^\\d{6}$").test(code.value)) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_VERIFICATION_CODE];
  }
});
const passwordError = computed(() => {
  if (!password.value) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_PASSWORD];
  }
  if (!new RegExp('^.{6,32}$').test(password.value)) {
    return language[LANGUAGE_KEY.PROMPT_INPUT_CORRECT_PASSWORD];
  }
});

const handlePasswordInput = () => {
};

// 高亮的表单行
const hightLightFormItem = ref(0);
// 表单聚焦
const onFocus = (rowIdx?: number) => {
  rowIdx && (hightLightFormItem.value = rowIdx);
};
const onBlur = () => {
  hightLightFormItem.value = 0;
};

const showPassword = ref(false);
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const isFormInvalid = computed(() => {
  return !!emailError.value ||  !!codeError.value || !!passwordError.value;
});

const onRecover = async () => {
  if (emailError.value) {
    Toast(emailError.value);
    return;
  } else if (codeError.value) {
    Toast(codeError.value);
    return;
  } else if (passwordError.value) {
    Toast(passwordError.value);
    return;
  }

  await ApiManager.getInstance().user.resetPassword({
    account: email.value,
    code: code.value,
    password: password.value
  }).then((response) => {
    if (response.code === 200) {
      Toast(language[LANGUAGE_KEY.PROMPT_TOAST_EDIT_SUCCESS]);
      props.onClose();
    } else {
      Toast(response.message);
    }
  }).catch((error) => {
    Toast(error.message);
  });
};


// 倒计时
let interval: any;
const countdown = ref(60);
const canSendCode = ref(true);
const countDown = () => {
  if (countdown.value <= 0) {
    countdown.value = 60;
    canSendCode.value = true;
    clearInterval(interval);
  } else {
    --countdown.value;
  }
};
const sendCode = async () => {
  // 没有填写邮箱号
  if (emailError.value) {
    Toast(emailError.value);
    return;
  }
  canSendCode.value = false;

  await ApiManager.getInstance().user.sendVerificationCode({
    type: VERIFY_CODE_TYPE.EMAIL,
    channel: VERIFY_CODE_CHANNEL.RESET_PASSWORD,
    number: email.value
  }).then((response) => {
    if (response.code === 200) {
      Toast(language[LANGUAGE_KEY.PROMPT_TOAST_SEND_SUCCESS]);
      interval = setInterval(countDown, 1000);
    } else {
      canSendCode.value = true;
      Toast(response.message);
    }
  }).catch((error) => {
    canSendCode.value = true;
    Toast(error.message);
  });

  
};
</script>